import { useAuthState } from "@/services/auth.service";
import type { FeatureModel } from "@/types/auth";
import {
  campaignStates,
  campaignStatuses,
  type CampaignState,
  type CampaignStatus,
  type LeadFeedback,
  type LeadStatus,
} from "@/types/campaign";
import { api, queryClient } from "@/utils/api";
import { isOneOf } from "@/utils/type-guards";
import * as Sentry from "@sentry/react";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { HTTPError } from "ky";
import { dynamicIconImports } from "lucide-react/dynamic";
import { useCallback, useMemo } from "react";

export type TriggerType = {
  event: string;
  label: string;
  description: string;
};

export type LeadAction = {
  categoryIcon: Extract<
    keyof typeof dynamicIconImports,
    "alarm-clock" | "thumbs-up" | "thumbs-down"
  >;
  status: LeadStatus;
  reason: string;
  description: string;
  hasFollowUpTime: boolean;
  presetFollowUp?: string | null;
  feedback?: string[];
  icon: keyof typeof dynamicIconImports;
};

export type AppConfiguration = {
  features: {
    canUseIntegration: boolean;
    canUseTriggers: boolean;
    canUseAdvancedFilters: boolean;
    canUseLiveNumbers: boolean;
    canUseContacts: boolean;
    canSendContactsToIntegration: boolean;
    canSendLiveNumbersToIntegration: boolean;
    canUpgradeCreditTier: boolean;
    canPurchaseBuffer: boolean;
  };
  baseCreditsPerMonth: number;
  extraCreditsPerMonth: number;
  creditsBuffer: number;
  extraCreditsPerMonthPrice: number;
  creditsRemaining: number;
  creditEstimate: number;
  creditEstimateNextPeriod: number;
  creditEstimateRestOfPeriod: number;
  creditsRenewalDate: string | null;
  labelFilters: LabelInfo[];
  creditUpgradeOptions: Record<number, number>;
  creditBufferOptions: {
    credits: number;
    price: number;
  }[];
  campaignStates: {
    name: string;
    status: CampaignStatus;
    state: CampaignState | null;
    color: string | null;
  }[];
  leadStatusTypes: Record<string, string>;
  leadStatuses: {
    name: string;
    displayName: string;
    filterable: boolean;
  }[];
  leadActions: LeadAction[];
  triggerTypes: TriggerType[];
  feedbackTypes: { feedback: LeadFeedback; description: string }[];
  versions: Record<
    "freemium" | "essentials" | "enterprise" | "pro",
    { features: FeatureModel[]; price: number }
  >;
  subscription: {
    trial: boolean;
    from: string | null;
    to: string | null;
    expired: boolean;
  };
};

export interface LabelInfo {
  labelId: string;
  prettyName: string;
  description: string | null;
}

/**
 * Fetches the app configuration from the API
 */
const fetchAppConfiguration = async (): Promise<AppConfiguration> => {
  const data = await api
    .get<AppConfiguration>("apps/sales/configuration")
    .json();
  return data;
};

/**
 * Query options for fetching app configuration
 */
export const appConfigurationQueryOptions = queryOptions({
  queryKey: ["configuration"],
  gcTime: Infinity,
  staleTime: Infinity,
  queryFn: fetchAppConfiguration,
  retry: (failureCount, error) => {
    if (failureCount >= 4) return false;
    // Don't retry if user is unauthorized
    if (error instanceof HTTPError && error.response.status === 401) {
      return false;
    }
    return true;
  },
});

/**
 * Hook to fetch app configuration data
 */
export const getAppConfigurationFromCache = () =>
  queryClient.getQueryData<AppConfiguration>(
    appConfigurationQueryOptions.queryKey,
  );

/**
 * Hook to access app configuration data
 */
export const useAppConfiguration = () => {
  const { data: authData } = useAuthState();
  return useQuery({
    ...appConfigurationQueryOptions,
    enabled: !!authData,
  });
};

export const getCombinedCampaignStatuses = (appConfig?: AppConfiguration) => {
  const getLongName = (_status: string, _state: string | null) => {
    const status = isOneOf(_status, ...campaignStatuses) ? _status : null;

    if (!status) {
      Sentry.captureException({
        title: "Unhandled combined campaign status",
        status: _status,
      });
    }

    const state =
      _state !== null && isOneOf(_state, ...campaignStates) ? _state : null;

    if (!state && _state !== null) {
      Sentry.captureException({
        title: "Unhandled combined campaign state",
        state: _state,
      });
    }

    switch (status) {
      case "Inactive":
        return "Inaktiv kampagne";
      case "Active":
        switch (state) {
          case "Normal":
            return "Aktiv kampagne";
          case "NearlyDepleted":
            return "Kampagnen udløber snart";
        }
        break;
      case "Ended":
        return "Kampagnen er afsluttet";
      case "Archived":
        return "Kampagnen er arkiveret";
    }

    return null;
  };

  const getColorClassName = (color: string | null) => {
    switch (color) {
      case "green":
        return "text-green-600 dark:text-green-400";
      case "yellow":
        return "text-yellow-500 dark:text-yellow-300";
      case null:
        return null;
      default:
        Sentry.captureException({
          title: "Unhandled combined campaign status color",
          color,
        });
        return null;
    }
  };

  const combinedCampaignStatuses =
    appConfig?.campaignStates.map((item) => ({
      ...item,
      longName: getLongName(item.status, item.state) ?? item.name,
      color: getColorClassName(item.color),
    })) ?? [];

  const combinedCampaignStatusesMap = new Map(
    combinedCampaignStatuses.map((item) => [
      `${item.status},${item.state ?? "*"}`,
      item,
    ]),
  );

  const getCombinedCampaignStatus = (
    status: CampaignStatus,
    state: CampaignState,
  ) =>
    combinedCampaignStatusesMap.get(`${status},${state}`) ??
    combinedCampaignStatusesMap.get(`${status},*`) ??
    null;

  return {
    combinedCampaignStatuses,
    combinedCampaignStatusesMap,
    getCombinedCampaignStatus,
  };
};

export type CombinedCampaignStatus = ReturnType<
  typeof getCombinedCampaignStatuses
>["combinedCampaignStatuses"][number];

export const useCombinedCampaignStatuses = () => {
  const { data: appConfig } = useAppConfiguration();

  return useMemo(() => getCombinedCampaignStatuses(appConfig), [appConfig]);
};

export const useLeadStatuses = () => {
  const { data: appConfig } = useAppConfiguration();

  const map = useMemo(
    () =>
      new Map(appConfig?.leadStatuses.map((status) => [status.name, status])),
    [appConfig?.leadStatuses],
  );

  const getLabel = useCallback(
    (status: string) => map.get(status)?.displayName ?? status,
    [map],
  );

  return {
    leadStatusMap: map,
    getLeadStatusLabel: getLabel,
  };
};

const defaultFeatures: AppConfiguration["features"] = {
  canUseIntegration: false,
  canUseTriggers: false,
  canUseAdvancedFilters: false,
  canUseLiveNumbers: false,
  canUseContacts: false,
  canSendContactsToIntegration: false,
  canSendLiveNumbersToIntegration: false,
  canUpgradeCreditTier: false,
  canPurchaseBuffer: false,
};

/**
 * Hook to access feature flags from the app configuration.
 * Returns a features object with safe defaults (all false) when config is loading.
 */
export function useFeatures() {
  const { data: appConfig } = useAppConfiguration();
  return appConfig?.features ?? defaultFeatures;
}
